import { validateUrl } from 'src/data/validation';
import { cleanUrl } from 'src/lib/url';

const POPULAR_TLDS_STRING =
  'com|org|net|edu|gov|mil|int|io|ai|co|tv|me|xyz|info|biz|us|ca|uk|de|jp|fr|au|ru|cn|br|es|it|nl|se|ch|in|mx|za|sg|pl|ar|kr|tr|vn|be|COM|ORG|NET|EDU|GOV|MIL|INT|IO|AI|CO|TV|ME|XYZ|INFO|BIZ|US|CA|UK|DE|JP|FR|AU|RU|CN|BR|ES|IT|NL|SE|CH|IN|MX|ZA|SG|PL|AR|KR|TR|VN|BE';

// Update: Added lookbehind to ensure the URL is not part of an email (not preceded by '@').
const HTTP_URL_REGEX =
  '(?<!@)https?:\\/\\/(?:www\\.)?\\w{1}[-a-zA-Z0-9@:%._\\+~#=]{0,256}\\.[a-zA-Z0-9()]{1,63}\\b(?:[-a-zA-Z0-9@\\(\\):%_\\+.~#?&\\/=]*)';
const WWW_URL_REGEX =
  '(?<!@)www\\.[\\-a-zA-Z0-9]+(\\.{1}[\\-a-zA-Z0-9]+)*\\.([a-zA-Z]{2,5})+((\\/[\\S]+))?\\b';
const NON_HTTP_WWW_URL_REGEX =
  '(?<!@)\\b[\\-a-zA-Z0-9]+(\\.{1}[\\-a-zA-Z0-9]+)*\\.(' +
  POPULAR_TLDS_STRING +
  '){1}((\\/[\\S]+))?\\b';

const createRegex = (regexStr, content) => {
  try {
    const regex = RegExp(regexStr, 'gu');
    const regexResults = content.match(regex);
    const result = [];

    if (regexResults && regexResults.length) {
      regexResults.forEach((match) => {
        if (match && validateUrl(match).valid === true) {
          result.push(match);
        }
      });
    }

    return result;
  } catch {
    return [];
  }
};

const removeDuplicateUrls = (urls) => {
  const urlMap = new Map();
  for (const url of urls) {
    const cleanedUrl = cleanUrl(url);
    if (!urlMap.has(cleanedUrl) || url.length > urlMap.get(cleanedUrl).length) {
      urlMap.set(cleanedUrl, url);
    }
  }
  return Array.from(urlMap.values());
};

const removeUnwantedParentheses = (urls) => {
  for (let i = 0; i < urls.length; i++) {
    // if last characters is a closing parentheses and there is no opening parentheses remove the closing one
    if (urls[i].slice(-1) === ')' && !urls[i].includes('(')) {
      urls[i] = urls[i].slice(0, -1);
    }
  }
  return urls;
};

export const extractUrls = (content, limit) => {
  const httpRegexResult = createRegex(HTTP_URL_REGEX, content);
  const wwwUrlRegexResult = createRegex(WWW_URL_REGEX, content);
  const nonHttpRegexResult = createRegex(NON_HTTP_WWW_URL_REGEX, content);

  const urls = httpRegexResult.concat(wwwUrlRegexResult, nonHttpRegexResult);
  const urlsRemoveParentheses = removeUnwantedParentheses(urls);
  const urlsTrimmed = urlsRemoveParentheses.map((url) => url.trim());
  const urlsUnique = removeDuplicateUrls(urlsTrimmed);

  return { urls: urlsUnique.slice(0, limit), overLimit: urlsUnique.length > limit };
};
const getAllIndicesOfSubtext = (searchStr, content) => {
  const searchStrLen = searchStr.length;

  if (searchStr.length === 0) {
    return [];
  }

  let startIndex = 0;
  let index;
  const indices = [];

  while ((index = content.indexOf(searchStr, startIndex)) > -1) {
    indices.push(index);
    startIndex = index + searchStrLen;
  }

  return indices;
};

/**
 *
 * @param content
 * @return {{indices: [{start: number, end: number}], url: string}[]}
 *
 * return example:
 * [{
 *   url: "https://abc.com/",
 *   indices: [{
 *     start: 0,
 *     end: 16,
 *   },{
 *     start: 544,
 *     end: 560
 *   }]
 * }]
 */
export const getUrlsIndicesInContent = (content) => {
  const uniqueUrls = extractUrls(content);

  return uniqueUrls.map((url) => {
    return {
      url,
      indices: getAllIndicesOfSubtext(url, content),
    };
  });
};

export const areAllUrlsExtracted = (urls, urlsWithExtraction) => {
  return urls.every((url) => Object.hasOwn(urlsWithExtraction, url));
};

export const getSuccessfulExtractions = (extractions) => {
  return Object.fromEntries(Object.entries(extractions).filter(([_, value]) => value !== null));
};
